/**
 * First we will load all of this project's JavaScript dependencies which
 * includes React and other helpers. It's a great starting point while
 * building robust, powerful web applications using React + Laravel.
 */

require('./bootstrap');
require('Helpers/tooltipInit');
require('datatables.net-dt');
require('datatables.net-rowgroup-dt');

import * as Sentry from '@sentry/react';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import Sortable from 'sortablejs/modular/sortable.complete.esm.js';

import i18n from './modules/i18n';

import * as ApiUtils from 'Helpers/apiUtils';
import { idempotencyHeader } from 'Helpers/apiUtils';
import * as CardUtils from 'Helpers/cardUtils';
import * as currencyFormat from 'Helpers/currencyFormat';
import * as DateUtils from 'Helpers/dateUtils';
import * as formatService from 'Helpers/formatService';
import * as InputUtils from 'Helpers/inputUtils';
import * as localeService from 'Helpers/localeService';
import * as marginPercentage from 'Helpers/marginPercentage';
import * as MenuUtils from 'Helpers/menuUtils';
import * as SearchUtils from 'Helpers/searchUtils';
import { shouldSuppressReportDialog } from 'Helpers/sentryReportDialog';

import { CONTINGENCY_NOTICE_HEADER, showContingencyNotice, showContingencyNoticeFromHeader, showStoredContingencyNotice } from 'Scripts/contingencyNotice';
import { promptForApprovalExplanation } from 'Scripts/explanationPrompt';
import { showFlowbiteModal, hideFlowbiteModal, initFlowbiteModal, initializeFlowBiteModals } from 'Scripts/initializeFlowBiteModals';
import { showFlowbiteTab, initFlowbiteTab, initializeFlowBiteTabs } from 'Scripts/initializeFlowBiteTabs';
import 'Scripts/actualsVsBudgetTabHistory';
import 'Scripts/sidePanel';

import 'react-popper-tooltip/dist/styles.css';
import 'flowbite';

window.$t = i18n?.t || ((s) => s);
window.Sortable = Sortable;
window.ApiUtils = ApiUtils;
window.DateUtils = DateUtils;
window.SearchUtils = SearchUtils;
window.CardUtils = CardUtils;
window.MenuUtils = MenuUtils;
window.InputUtils = InputUtils;
window.formatSevice = formatService;
// The currency formatting engine. Exposed on window because the classic scripts
// (cashflow-new.js, expenditureTable.js and friends) are mix.copy'd rather than
// bundled, so they cannot import an ES module. app.js always runs before them.
window.InbudCurrency = currencyFormat;
// Margin % — the single implementation, on window for the same reason: the
// department / cost-centre and Expenditure summary tables live in expenditureTable.js.
window.InbudMargin = marginPercentage;
// The zero-as-dash figure rule, for the same mix.copy'd scripts.
window.InbudFigure = { formatFigure: formatService.formatFigure, ZERO_DASH: formatService.ZERO_DASH };
window.localeService = localeService;
window.showFlowbiteModal = showFlowbiteModal;
window.hideFlowbiteModal = hideFlowbiteModal;
window.initFlowbiteModal = initFlowbiteModal;
window.promptForApprovalExplanation = promptForApprovalExplanation;
window.showContingencyNotice = showContingencyNotice;
window.showContingencyNoticeFromHeader = showContingencyNoticeFromHeader;
window.initializeFlowBiteModals = initializeFlowBiteModals;
window.showFlowbiteTab = showFlowbiteTab;
window.initFlowbiteTab = initFlowbiteTab;
window.initializeFlowBiteTabs = initializeFlowBiteTabs;
window.idempotencyHeader = idempotencyHeader;
window.Sentry = Sentry;

// Where returned contingency went rides on the response of the request that returned
// it: jQuery's here, axios' in the shared client's interceptor.
$(document).ajaxComplete((event, xhr) => showContingencyNoticeFromHeader(xhr.getResponseHeader(CONTINGENCY_NOTICE_HEADER)));
$(() => showStoredContingencyNotice());

// Pusher
window.Pusher = Pusher;

// Echo
try {
    window.Echo = new Echo({
        broadcaster: 'pusher',
        key: process.env.MIX_PUSHER_APP_KEY,
        cluster: process.env.MIX_PUSHER_APP_CLUSTER,
        forceTLS: true,
    });
} catch (error) {
    console.error(error);
}

Sentry.setUser({
    name: context_user?.name,
    email: context_user?.email,
});

Sentry.setTag('organisation_name', context_organisation?.name);
Sentry.setTag('event_name', context_event?.name);

Sentry.setContext('Organisation', context_organisation);
Sentry.setContext('Event', context_event);

Sentry.init({
    dsn: process.env.MIX_SENTRY_LARAVEL_DSN,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration(),
        Sentry.captureConsoleIntegration(),
        Sentry.feedbackIntegration({
            showName: false,
            showEmail: false,
            showBranding: false,
            autoInject: false,
            messagePlaceholder: window.$t('Please describe the bug.'),
            triggerLabel: window.$t('Report a Bug'),
            useSentryUser: {
                name: 'name',
                email: 'email',
            },
        }),
    ],
    ignoreErrors: ['Non-Error exception captured'],
    environment: process.env.MIX_APP_ENV || 'production',
    tracesSampleRate: parseFloat(process.env.MIX_SENTRY_TRACES_SAMPLE_RATE) || 0.1,
    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: ['localhost', `/^${_.escapeRegExp(process.env.MIX_APP_URL)}/`],
    profilesSampleRate: parseFloat(process.env.MIX_SENTRY_PROFILES_SAMPLE_RATE) || 0.1,
    replaysSessionSampleRate: parseFloat(process.env.MIX_SENTRY_REPLAYS_SESSION_SAMPLE_RATE) || 0.1,
    replaysOnErrorSampleRate: parseFloat(process.env.MIX_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE) || 1.0,
    debug: process.env.MIX_SENTRY_DEBUG === 'true',
    beforeSend(event, hint) {
        if (event.exception && event.event_id && !shouldSuppressReportDialog(event, hint)) {
            Sentry.showReportDialog({
                eventId: event.event_id,
                user: {
                    name: context_user?.name,
                    email: context_user?.email,
                },
                title: $t('It looks like we\x27re having issues.'),
                subtitle: $t('Our team has been notified.'),
                subtitle2: $t('If you’d like to help, tell us what happened below.'),
                labelName: $t('Name'),
                labelEmail: $t('Email'),
                labelComments: $t('What happened?'),
                labelClose: $t('Close'),
                labelSubmit: $t('Submit'),
                errorGeneric: $t('An unknown error occurred while submitting your report. Please try again.'),
                errorFormEntry: $t('Some fields were invalid. Please correct the errors and try again.'),
                successMessage: $t('Your feedback has been sent. Thank you!'),
            });
        }
        return event;
    },
});

// require('@fortawesome/fontawesome-free/js/all.js');
require('jquery-ui/ui/widgets/datepicker.js');
require('jquery-ui/ui/widgets/slider.js');
require('jquery-ui/ui/widgets/sortable.js');
require('select2/dist/js/select2.js');
require('jquery-timepicker/jquery.timepicker.js');
require('jquery-ui/ui/widgets/sortable.js');
require('jquery-ui/ui/widgets/draggable.js');
require('jquery-ui/ui/widgets/droppable.js');

/**
 * Next, we will create a fresh React component instance and attach it to
 * the page. Then, you may begin adding components to this application
 * or customize the JavaScript scaffolding to fit your unique needs.
 */

require('./components/templates/TemplateAssign');
require('./components/packs/ExpensePackApp');
require('./components/charts/EventChart');
require('./components/budget_tags/BudgetTagsSection');
require('./components/budget_tags/BulkApplyBudgetTagModal');
require('./components/budget_tags/view/BudgetTagView');
require('./components/budgets/Budgets');
require('./components/budgets/BudgetsItem');
require('./components/attachments_expenses/ExpenseAttachmentUpload');
require('./components/attachments');
require('./components/admin/Users');
require('./components/admin/approvement/ApprovalSettings');
require('./components/admin/Permissions');
require('./components/admin/Accounts');
require('./components/admin/EventsCreate');
require('./components/admin/EventsClone');
require('./components/expense_fund_approvals/ExpenseFundApprovals');
require('./components/position_fund_approvals/PositionFundApprovals');
require('./components/suppliers/SuppliersIndex');
require('./components/suppliers/SuppliersCreate');
require('./components/supplier_contracts/SupplierContractsIndex');
require('./components/procurement/ProcurementIndex');
require('./components/procurement/face/DeptProcurementFace');
require('./components/procurement_policies/ProcurementPoliciesIndex');
require('./components/procurement_policies/ProcurementPoliciesCreate');
require('./components/procurement_plan/ProcurementPlanIndex');
require('./components/purchase_requisitions/PurchaseRequisitionsIndex');
require('./components/purchase_orders/PurchaseOrdersCreate');
require('./components/revenue-commitment/RevenueCommitmentIndex');
require('./components/revenue-contracts/RevenueContractsIndex');
require('./components/revenues-commitments/RevenueCommit');
require('./components/ticketings-commitments/TicketingCommit');
require('./components/sponsors-commitments/SponsorCommitmentModal');
require('./components/sponsors-commitments/SponsorCommit');
require('./components/purchase_orders/PurchaseOrdersIndex');
require('./components/committed_expenditures/UncommittedExpendituresCreate');
require('./components/committed_expenditures/CommittedExpendituresIndex');
require('./components/committed_expenditures/CommittedExpendituresCreate');
require('./components/event_info/MasterplanTable');
require('./components/budget_codes/BudgetCodesIndex');
require('./components/dashboard/KeyMetrics');
require('./components/dashboard/insights/Insights');
require('./components/actual_vs_budget_report/ActualVsBudgetReport');
require('./components/departments_budgets_reports/DepartmentsBudgetsReport');
require('./components/bill_of_quantities/BillOfQuantities');
require('./components/cashflow_central_module/CashflowCentralModule');
require('./components/instructions/InstructionsIndex');
require('./components/instructions/AdminInstructionsIndex');
require('./components/instructions/AdminInstructionEdit');
require('./components/instructions/AdminInstructionCreate');
require('./components/unallocated_expenses/UnallocatedExpensesIndex');
require('./components/evaluation/scoring_criteria/ScoringCriteriaIndex');
require('./components/evaluation/template/EvaluationTemplateCreate');
require('./components/evaluation/template/edit/EvaluationTemplateEdit');
require('./components/evaluation/template/evaluate/EvaluateCompanies');
require('./components/evaluation/template/EvaluationTemplateList');
require('./components/evaluation/template/EvaluationTemplateShow');
require('./components/event/RecalculateTotalButton');
require('./components/event_impacts/InputsIndex');
require('./components/event_impacts/EventImpactDashboard');
require('./components/workforce/WorkforceOrganigramIndex');
require('./components/venues/VenueOrganigramIndex');
require('./components/cost_allocation/CostAllocationIndex');
require('./components/supplier_framework/SupplierFrameworkIndex');
require('./components/event_info/ClientGroupNumbers');
require('./components/event_info/spectator_daily_movements/SpectatorDailyMovementsIndex');
require('./components/admin/users/activity/UserActivityIndex');
require('./components/admin/audit_logs/AuditLogsIndex');
require('./components/budget_change_management/BudgetChangeManagementIndex');
require('./components/budget_change_management/LocationMovements');
require('./components/purchase_orders/reporting/PurchaseOrderReportingIndex');
require('./components/purchase_orders/reporting/ProcurementHistoryReportingIndex');
require('./components/contractor_scheduling/ContractorSchedulingIndex');
require('./components/sports/athletes/SportAthletesIndex');
require('./components/sports/officials/SportOfficialsIndex');
require('./components/sports/allocations/SportRevenuesExpenditures');
require('./components/radio/GeneralRiskFramework');
require('./components/radio/RadioApp');
require('./components/radio/GeneralRisk');
require('./components/radio/GeneralIssue');
require('./components/radio/GeneralDecision');
require('./components/supplier_contract_terms/SupplierContractTermsIndex');
require('./components/budget_change_requests/ChangeRequestsIndex');
require('./components/budget_change_requests/LocationChangeRequests');
require('./components/budget_change_requests/PendingLineChanges');
require('./components/budget_approval_requests/HasPendingRequestWarning');
require('./components/supplier_contract_terms/SupplierContractTermOverduesIndex');
require('./components/central_assumptions/CentralAssumptionsIndex');
require('./components/services/ServicesIndex');
require('./components/expenser_library_templates/ExpenseLibraryTemplatesIndex');
require('./components/expenser_library_templates/ExpenseLibraryTemplatesShow');
require('./components/organisations/events/OrganisationEventsIndex');
require('./components/organisations/my_events/MyEventsIndex');
require('./components/organisations/my_time_record/MyTimeRecordIndex');
require('./components/organisations/my_time_summary/MyTimeSummaryIndex');
require('./components/organisations/CreateOrganisationModal');
require('./components/organisations/OrganisationSettingsIndex');
require('./components/organisations/users/OrganisationUsersPage');
require('./components/packs/view/PackTable');
require('./components/organisations/rate_card/OrganisationRateCardIndex');
require('./components/organisations/fx/OrganisationFxIndex');
require('./components/organisations/summary/OrganisationSummaryIndex');
require('./components/supplier_contract_reports/SupplierContractReportsIndex');
require('./components/organisations/time_report/ProjectTimeReportIndex');
require('./components/organisations/time_overview/ProjectsTimeOverviewIndex');
require('./components/organisations/budget_overview/ProjectsBudgetOverviewIndex');
require('./components/auth/MfaConfirmIndex');
require('./components/auth/MfaChallenge');
require('./components/supplier_contract_reports/SupplierContractReportsIndex');
require('./components/supplier_portal_management/SupplierPortalManagementIndex');
require('./components/supplier_portal_management/ExternalSupplierPortal');
require('./components/supplier_portal/SupplierPortal');
require('./components/partial/expense/ExpenseRateCardPicker');
require('./components/project_dashboard/ProjectDashboardIndex');
require('./components/project_plan/ProjectPlanIndex');
require('./components/project_plan/SourcedProjectIndex');
require('./components/partial/expense/ExpenseQuantityEdit');
require('./components/api_clients/ApiClientsIndex');
require('./components/api_clients/ApiClientItemIndex');
require('./components/home/HomeIndex');
require('./components/home/records/HomeRecordPage');
require('./components/event_setup/EventSetup');
require('./components/procurement_approvals/ProcurementApprovalsIndex');
require('./components/setup_wizard/SetupWizard');
require('./components/event/OrganisationalCharts');
require('./components/tags/TagsIndex');
require('./components/sessions/SessionsTab');
require('./components/admin/subscription_plans/SubscriptionsPlansIndex');
require('./components/integrations/IntegrationsSetupIndex');
require('./components/integrations/activation/ToggleGlCodesSync');
require('./components/simple_budget_expense/UncommittedTable');
require('./components/procurement_plan/DirectCommit');
require('./components/reporting/ReportingOverviewTable');
require('./components/dashboard/KeyMetricsFinancialSummary');
require('./components/procurement_plan/ProcurementPlanChart');
require('./components/procurement_plan/DeptProcurementPlanIndex');
require('./components/venues/VenueBudget');
require('./components/venues/VenueWorkforceDetails');
require('./components/venues/VenueContractors');
require('./components/department/DepartmentCashflowModule');
require('./components/department/AddToBudgetStructureModal');
require('./components/procurement_plan/ProcurementPlanChart');
require('./components/actual_costs/reconciliation/ActualCostsAllocationIndex');
require('./components/actual_costs/reconciliation/ReconciliationIndex');
require('./components/actual_costs/upload/ActualsUploadPage');
require('./components/actual_costs/archived_reports/ArchivedActualCostsReports');
require('./components/actual_costs/DepartmentActualsReportTabs');
require('./components/integrations/activation/ToggleActualCostsSync');
require('./components/milestones/MilestonesEdit');
require('./components/reporting/PositionExpenditureOverviewTable');
require('./components/summary_schedule/SummaryIndex');
require('./components/ticketing/TicketingSummaryTable');
require('./components/cashflow-settings/CashflowSettingsScreen');
require('./components/report_budget_risks_opportunities/ReportBudgetRisksOpportunitiesIndex');
require('./components/cashflow_forecast_report/CashflowForecastReport');
require('./components/workforce/assumption/WorkforceAssumptionIndex');
require('./components/workforce/assumption/WorkforcePayrate');
require('./components/positions/PositionsTable');
require('./components/positions/PositionWorkforceQuantityIntercept');
require('./components/tracking_categories/TrackingCategoriesIndex');
require('./components/report_centre/budget_by_venues/BudgetByVenuesArchiveIndex');
require('./components/report_centre/budget_by_dimension/BudgetByVenueReport');
require('./components/report_centre/budget_by_dimension/BudgetByEventReport');
require('./components/report_centre/budget_by_dimension/BudgetByDimensionOnlineReview');
require('./components/report_centre/budget_by_dimension/DimensionBudgetReview');
require('./components/report_centre/management_pack/ManagementPackBuilder');
require('./components/report_centre/modules/ReportModuleGallery');
require('./components/report_centre/modules/ReportViewChrome');
require('./components/report_centre/budgets_by_tag/BudgetsByTagReport');
require('./components/report_centre/unified/ReportCentre');
require('./components/report_centre/project_plan/ProjectPlanReport');
require('./components/report_centre/radio/RadioReport');
require('./components/seat_kills/SeatKillsIndex');
require('./components/position_activities/PositionActivitiesApp');
require('./components/supplier_contracts/ArchivedSupplierContractReportsIndex');
require('./components/contracts/ExpenditureContractsModal');
require('./components/contracts/RevenueContractsModal');
require('./components/contracts/SponsortierContractsModal');
require('./components/mark_up_categories/MarkUpCategoriesTable');
require('./components/departments_budgets_reports/OnlineReview');
require('./components/departments_budgets_reports/DepartmentsBudgetsReview');
require('./components/departments_budgets_reports/DepartmentsBudgetsSnapshot');
require('./components/venue_raid/VenueRaidApp');
require('./components/dept_raid/DeptRaidApp');
require('CommonComponents/modals/ExpenseRiskOpportunityCreateModal');
require('CommonComponents/modals/ExpenseRiskOpportunityListModal');
require('./components/quantity_link/QuantityLinkApp');
require('./components/cashflow_phasing/CashflowPhasingDialogApp');
require('./components/quantity_link/QuantityImpactPreviewDialog');
require('./components/quantity_link/QuantityBulkImpactPreviewDialog');
require('./components/quantity_link/DependentLinesPanel');
// The control rail must be required before the dashboard: it installs
// window.peopleControls, which the dashboard subscribes to.
require('./components/people/PeopleControlRail');
require('./components/people/PeopleDashboard');
